import React, { useState } from 'react';
import { Crown, Zap, CheckCircle, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { STRIPE_PRODUCTS, calculateYearlySavings } from '@/stripe-config';
import { analytics } from '@/utils/analytics';
import { metaPixel } from '@/utils/meta-pixel';
import { formatPrice, formatMonthlyPrice } from '@/utils/formatPrice';

interface PricingPlansProps {
  showOneTimeOnly?: boolean;
  showSubscriptionOnly?: boolean;
  onSelectPlan: (plan: typeof STRIPE_PRODUCTS[0]) => void;
  selectedPlanId?: string;
  loading?: boolean;
}

interface SubscriptionCardProps {
  isPro: boolean;
  onSelectPlan: (plan: typeof STRIPE_PRODUCTS[0]) => void;
  selectedPlanId?: string;
  loading?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ isPro, onSelectPlan, selectedPlanId, loading }) => {
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const monthlyProduct = STRIPE_PRODUCTS.find(
    p => p.mode === 'subscription' && !p.isLegacy && p.billingPeriod === 'monthly' && (p.isPro || false) === isPro
  );
  const yearlyProduct = STRIPE_PRODUCTS.find(
    p => p.mode === 'subscription' && !p.isLegacy && p.billingPeriod === 'yearly' && (p.isPro || false) === isPro
  );

  const product = period === 'monthly' ? monthlyProduct : yearlyProduct;
  if (!product) return null;

  const isSelected = selectedPlanId === product.id;
  const savings = monthlyProduct && yearlyProduct
    ? calculateYearlySavings(monthlyProduct.price, yearlyProduct.price)
    : null;

  const handleClick = () => {
    analytics.trackEvent({
      action: 'click',
      category: 'pricing',
      label: `plan_${product.id}`,
      custom_parameters: {
        plan_id: product.id,
        plan_name: product.name,
        price: product.price,
        mode: product.mode,
        billing_period: product.billingPeriod,
        is_pro: product.isPro || false
      }
    });
    metaPixel.trackSubscription(product.id, product.price, 'EUR');
    console.log('🔍 [DEBUG] Plan clicked:', product.id, product.mode, product.billingPeriod, product.isPro);
    onSelectPlan(product);
  };

  return (
    <Card
      className={`relative transition-all duration-300 hover:shadow-xl ${
        isPro ? 'border-2 border-blue-600 shadow-lg' : 'border border-gray-200 hover:border-blue-300'
      } ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
    >
      {isPro && (
        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
          <Badge className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2 text-sm font-medium flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Branding personnalisé
          </Badge>
        </div>
      )}

      <CardHeader className="text-center">
        <div className="flex items-center justify-center mb-4">
          {isPro ? (
            <Building2 className="w-8 h-8 text-blue-600" />
          ) : (
            <Crown className="w-8 h-8 text-blue-500" />
          )}
        </div>

        <CardTitle className="text-2xl">{product.name}</CardTitle>
        <CardDescription className="mb-4">{monthlyProduct?.description ?? product.description}</CardDescription>

        {/* Inline billing toggle */}
        <div className="flex items-center justify-center bg-gray-100 rounded-lg p-1 mb-4 mx-auto w-fit gap-1">
          <button
            onClick={() => setPeriod('monthly')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              period === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setPeriod('yearly')}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
              period === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Annuel
            {savings !== null && (
              <span className="bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded">
                -{savings}%
              </span>
            )}
          </button>
        </div>

        <div className="mb-2">
          <div className="flex items-baseline justify-center gap-2">
            <span className="text-4xl font-bold text-gray-900">
              {formatPrice(product.price, { includeTax: product.isTaxIncluded, showTaxLabel: true })}
            </span>
          </div>

          {period === 'yearly' ? (
            <>
              <div className="text-sm text-gray-600 mt-1">/an</div>
              <div className="text-sm text-gray-500 mt-2">
                soit {formatMonthlyPrice(product.price)}{product.isTaxIncluded ? ' TTC' : ' HT'}/mois
              </div>
              <div className="mt-2">
                <Badge className="bg-green-100 text-green-700 border-green-200">
                  2 mois offerts
                </Badge>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-600 mt-1">/mois</div>
              <div className="text-sm text-gray-500 mt-2">sans engagement</div>
            </>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {product.features.map((feature, index) => {
            const isBrandingFeature = feature.toLowerCase().includes('logo') ||
                                     feature.toLowerCase().includes('entreprise') ||
                                     feature.toLowerCase().includes('visioconférence') ||
                                     feature.toLowerCase().includes('visioconference');
            return (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                  isPro && isBrandingFeature ? 'text-blue-600' : 'text-green-500'
                }`} />
                <span className={`text-gray-700 ${
                  isPro && isBrandingFeature ? 'font-semibold text-blue-900' : ''
                }`}>
                  {feature}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>

      <CardFooter>
        <Button
          onClick={handleClick}
          disabled={loading || isSelected}
          loading={loading && isSelected}
          className={`w-full ${
            isPro
              ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white hover:from-blue-700 hover:to-blue-900 shadow-lg hover:shadow-xl'
              : ''
          }`}
          variant={isPro ? 'primary' : 'outline'}
          size="lg"
        >
          {isSelected ? 'Sélectionné' : "S'abonner"}
        </Button>

        <p className="text-center text-sm text-gray-500 mt-4">
          {period === 'monthly' ? 'Annulable à tout moment' : 'Engagement annuel - Facturation en une fois'}
        </p>
      </CardFooter>
    </Card>
  );
};

export const PricingPlans: React.FC<PricingPlansProps> = ({
  showOneTimeOnly = false,
  showSubscriptionOnly = false,
  onSelectPlan,
  selectedPlanId,
  loading = false,
}) => {
  const oneTimeProduct = STRIPE_PRODUCTS.find(p => p.mode === 'payment' && !p.isLegacy);

  const handleOneTimeClick = (product: typeof STRIPE_PRODUCTS[0]) => {
    analytics.trackEvent({
      action: 'click',
      category: 'pricing',
      label: `plan_${product.id}`,
      custom_parameters: {
        plan_id: product.id,
        plan_name: product.name,
        price: product.price,
        mode: product.mode,
        is_pro: false
      }
    });
    metaPixel.trackCreditPurchase(1, product.price, 'EUR');
    console.log('🔍 [DEBUG] Plan clicked:', product.id, product.mode);
    onSelectPlan(product);
  };

  if (showOneTimeOnly) {
    if (!oneTimeProduct) return null;
    const isSelected = selectedPlanId === oneTimeProduct.id;
    return (
      <div className="max-w-sm mx-auto">
        <Card className={`relative transition-all duration-300 hover:shadow-xl border border-gray-200 hover:border-blue-300 ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}>
          <CardHeader className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Zap className="w-8 h-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl">{oneTimeProduct.name}</CardTitle>
            <CardDescription className="mb-4">{oneTimeProduct.description}</CardDescription>
            <div className="mb-2">
              <span className="text-4xl font-bold text-gray-900">
                {formatPrice(oneTimeProduct.price, { includeTax: oneTimeProduct.isTaxIncluded, showTaxLabel: true })}
              </span>
              <div className="text-sm text-gray-600 mt-1">paiement unique</div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {oneTimeProduct.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 text-green-500" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter>
            <Button
              onClick={() => handleOneTimeClick(oneTimeProduct)}
              disabled={loading || isSelected}
              loading={loading && isSelected}
              variant="outline"
              size="lg"
              className="w-full"
            >
              {isSelected ? 'Sélectionné' : `Acheter (${formatPrice(oneTimeProduct.price, { includeTax: oneTimeProduct.isTaxIncluded, showTaxLabel: true })})`}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (showSubscriptionOnly) {
    return (
      <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto">
        <SubscriptionCard isPro={false} onSelectPlan={onSelectPlan} selectedPlanId={selectedPlanId} loading={loading} />
        <SubscriptionCard isPro={true} onSelectPlan={onSelectPlan} selectedPlanId={selectedPlanId} loading={loading} />
      </div>
    );
  }

  return (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-7xl mx-auto">
      {/* One-time card */}
      {oneTimeProduct && (() => {
        const isSelected = selectedPlanId === oneTimeProduct.id;
        return (
          <Card className={`relative transition-all duration-300 hover:shadow-xl border border-gray-200 hover:border-blue-300 ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}>
            <CardHeader className="text-center">
              <div className="flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-green-500" />
              </div>
              <CardTitle className="text-2xl">{oneTimeProduct.name}</CardTitle>
              <CardDescription className="mb-4">{oneTimeProduct.description}</CardDescription>
              <div className="mb-2">
                <span className="text-4xl font-bold text-gray-900">
                  {formatPrice(oneTimeProduct.price, { includeTax: oneTimeProduct.isTaxIncluded, showTaxLabel: true })}
                </span>
                <div className="text-sm text-gray-600 mt-1">paiement unique</div>
                <div className="text-sm text-gray-500 mt-2">sans engagement</div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {oneTimeProduct.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 text-green-500" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                onClick={() => handleOneTimeClick(oneTimeProduct)}
                disabled={loading || isSelected}
                loading={loading && isSelected}
                variant="outline"
                size="lg"
                className="w-full"
              >
                {isSelected ? 'Sélectionné' : `Acheter (${formatPrice(oneTimeProduct.price, { includeTax: oneTimeProduct.isTaxIncluded, showTaxLabel: true })})`}
              </Button>
            </CardFooter>
          </Card>
        );
      })()}

      {/* Subscription cards with inline toggle */}
      <SubscriptionCard isPro={false} onSelectPlan={onSelectPlan} selectedPlanId={selectedPlanId} loading={loading} />
      <SubscriptionCard isPro={true} onSelectPlan={onSelectPlan} selectedPlanId={selectedPlanId} loading={loading} />
    </div>
  );
};

export default PricingPlans;
